import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

/**
 * What a class activity must look like before it can be set.
 *
 * Mirrors the backend's `activities.validation.ts` — the server is the
 * authority, these rules only save a round trip. Keep the two in step.
 *
 * The one rule deliberately left to the server is whether the due date falls
 * inside the academic year: the session's own dates are not in the browser, and
 * guessing them here would reject a valid date on an institute whose year runs
 * differently.
 */

export const ACTIVITY_TYPES = [
  'INDIVIDUAL_PROJECT',
  'GROUP_ACTIVITY',
  'LAB_PRACTICAL',
  'PRESENTATION',
  'FIELD_RESEARCH',
  'ART_CRAFT',
] as const;

/**
 * How urgent the teacher says it is.
 *
 * Mirrors `ACTIVITY_PRIORITIES` on the server. It is what the parent panel's
 * Tasks table sorts and filters on — a family comparing what is due this
 * fortnight to decide what happens this evening.
 */
export const ACTIVITY_PRIORITIES = ['HIGH', 'MEDIUM', 'LOW'] as const;

/** Matches the server's ceiling — a term-long portfolio, and no typing slips. */
export const MAX_POINTS_CEILING = 500;

export const activitySchema = z.object({
  classSectionUuid: z.string().uuid('Choose a class'),
  subjectUuid: z.string().uuid('Choose a subject'),

  title: z
    .string()
    .trim()
    .min(3, 'Give the activity a title')
    .max(150, 'That title is too long'),

  activityType: z.enum(ACTIVITY_TYPES, {
    errorMap: () => ({ message: 'Choose what kind of activity this is' }),
  }),

  /**
   * How urgent this is. It is what a family reads their evening off in the
   * parent panel's Tasks table, which sorts and filters on it.
   */
  priority: z.enum(ACTIVITY_PRIORITIES, {
    errorMap: () => ({ message: 'Say how urgent this is' }),
  }),

  description: z
    .string()
    .trim()
    .min(10, 'Say what the children have to do')
    .max(1000, 'Keep the objective under 1000 characters'),

  instructions: z
    .string()
    .trim()
    .max(2000, 'Keep the instructions under 2000 characters')
    .transform((value) => value || undefined),

  dueDate: z
    .string()
    .trim()
    .min(1, 'Pick a due date')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15'),

  /**
   * Arrives as the string an `<input>` holds and leaves as a number, or as
   * `null` for an activity that is not scored — which is what an empty box
   * means here, rather than zero.
   */
  maxPoints: z
    .string()
    .trim()
    .transform((value) => (value === '' ? null : Number(value)))
    .refine(
      (value) => value === null || (Number.isInteger(value) && value >= 1 && value <= MAX_POINTS_CEILING),
      `Whole points between 1 and ${MAX_POINTS_CEILING}, or leave it empty for an unscored activity`,
    ),
});

/** What the form holds while it is being filled in — every field a string. */
export interface ActivityFormValues {
  classSectionUuid: string;
  subjectUuid: string;
  title: string;
  activityType: (typeof ACTIVITY_TYPES)[number];
  priority: (typeof ACTIVITY_PRIORITIES)[number];
  description: string;
  instructions: string;
  dueDate: string;
  maxPoints: string;
}

/** What the API takes, once parsed. */
export type ActivityPayload = z.infer<typeof activitySchema>;

/**
 * The resolver the activity form uses.
 *
 * `raw` keeps the form holding what the inputs hold. Without it the resolver
 * hands the submit handler the *transformed* values — points already a number,
 * empty instructions already `undefined` — while the form state stays strings,
 * so the declared `ActivityFormValues` would be a lie and the `parse()` that
 * builds the payload would throw on data that is perfectly valid.
 *
 * Annotated rather than cast, for the same reason: a cast here would hide
 * exactly that mismatch, which is how it went unnoticed the first time.
 */
export const activityFormResolver: Resolver<ActivityFormValues> = zodResolver(
  activitySchema,
  undefined,
  { raw: true },
);

/** A new activity starts as an individual project — the commonest kind. */
export function emptyActivityForm(
  classSectionUuid = '',
  subjectUuid = '',
  dueDate = '',
): ActivityFormValues {
  return {
    classSectionUuid,
    subjectUuid,
    title: '',
    activityType: 'INDIVIDUAL_PROJECT',
    // Ordinary unless the teacher says otherwise — the same default the column
    // carries, so a form left alone and a row written before the column existed
    // mean the same thing.
    priority: 'MEDIUM',
    description: '',
    instructions: '',
    dueDate,
    maxPoints: '20',
  };
}
